import { object, string, array } from 'yup';
import { BaseModel, CreateBaseModel } from '../base.model';
import { JsonObject } from '../../helpers';
import * as yup from 'yup';
import { IdentProviderModel } from '../ident-provider/ident-provider.model';
import { upperCase } from 'lodash';
import {
  OrgBasicInfo,
  OrgBranding,
  OrgExternalReferences,
  OrgIdentityConfig,
  OrgMode,
  OrgPolicyEngine,
} from '.';

export class OrgModel extends BaseModel {
  name: string;
  domains: string[];
  allowSupportAccess: boolean;
  identityConfig: OrgIdentityConfig;
  externalReferences?: OrgExternalReferences;
  branding: OrgBranding;
  mode: OrgMode;
  disablePromptRetention: boolean;
  conversationRetentionDays: number;
  requestMetadataRetentionDays: number;
  enablePortalChat: boolean;
  policyEngine: OrgPolicyEngine;

  schema = object({
    name: string().required('Organization Name is required'),
    domains: array().of(yup.string()).min(1, 'Your organization must have at least one domain'),
    branding: object({
      portalWelcome: object({
        message: string().max(1024, 'The welcome message must be no more than 1,024 characters'),
      }),
    }),
  });

  constructor(props: JsonObject = {}) {
    super(props);

    const data = props as Record<keyof OrgModel, unknown>;

    this.name = (data.name as string) || '';
    this.domains = (data.domains as string[]) || [];

    this.allowSupportAccess = Boolean(data.allowSupportAccess);
    this.identityConfig = {
      gcipTenantId: '',
      providers: [],
    };
    this.externalReferences = data.externalReferences as OrgExternalReferences;

    if (data.identityConfig) {
      this.identityConfig = data.identityConfig as OrgIdentityConfig;
      this.identityConfig.providers = this.identityConfig.providers.map(
        (data) => new IdentProviderModel(data as unknown as JsonObject)
      );
    }

    this.branding = (data.branding as OrgBranding) || {
      lightThemeLogo: '',
      darkThemeLogo: '',
      portalBadge: {
        image: '',
        darkImage: '',
        title: '',
      },
      portalWelcome: {
        title: '',
        message: '',
        learnMoreLink: '',
      },
      supportEmail: '',
      acceptableUsePolicyURL: '',
      favicon: '',
      warnings: {
        publicServiceBlocked: '',
        publicServiceSensitiveData: '',
        privateServiceIntentBlocked: '',
      },
    };

    this.mode = (data.mode as OrgMode) || 'platform';
    this.disablePromptRetention = !!data.disablePromptRetention;
    this.conversationRetentionDays = (data.conversationRetentionDays as number) || -1; // user event messages
    this.requestMetadataRetentionDays = (data.requestMetadataRetentionDays as number) || -1; // user event document
    this.policyEngine = (data.policyEngine as OrgPolicyEngine) || {
      version: 'v1',
      mergeStrategy: 'least-restrictive',
    };

    this.enablePortalChat = !!data.enablePortalChat;
  }

  get hasOIDC(): boolean {
    return this.identityConfig.providers.some(({ type }) => type === 'OIDC');
  }

  get hasSAML(): boolean {
    return this.identityConfig.providers.some(({ type }) => type === 'SAML');
  }

  get basicInfo(): OrgBasicInfo {
    return {
      name: this.name,
      mode: this.mode,
      policyEngine: this.policyEngine,
      enablePortalChat: this.enablePortalChat,
    };
  }

  get modeLabel(): string {
    return upperCase(this.mode || 'platform');
  }

  // zero data retention
  get hasZdr(): boolean {
    return !this.conversationRetentionDays && !this.requestMetadataRetentionDays;
  }
}

export class CreateOrgModel extends CreateBaseModel {
  allowSupportAccess: boolean;
  branding: OrgBranding;

  constructor(org: OrgModel) {
    super();
    this.allowSupportAccess = org.allowSupportAccess;
    this.branding = org.branding;
  }
}
